import { FunctionComponent, useState } from "react";
import { useForm } from 'react-hook-form';
import { yupResolver } from "@hookform/resolvers/yup";
import { ReactComponent as LogoHeader } from '../../assets/logo-color.svg';
import TrustedPartnerApplicationSchema from "../../utils/forms/schema/partner/TrustedPartnerApplicationSchema";


import {
    REGION_OPTIONS,
    EMPLOYEE_COUNT_OPTIONS,
    SERVICE_OPTIONS
} from "../../utils/types/TrustedPartnerTypes";


import { TextInputWithRegister, MultiSelectDropdown } from "../inputs";
import FileUpload from "../inputs/upload/FileUpload";
import { Button } from "../generic";


const TrustedPartnerApplicationForm: FunctionComponent = () => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    const {
        register,
        handleSubmit,
        formState: { errors }
    } = useForm({
        defaultValues: {
            companyName: "",
            companyRep: "",
            address: "",
            city: "",
            state: "",
            zip: "",
            regions: [] as string[],
            phone: "",
            email: "",
            employees: "",
            numberOfBranches: 1,
            reasonForApplication: "",
            whyClientVitals: "",
            websiteLink: "",
            servicesNeeded: [] as string[]
        },
        resolver: yupResolver(TrustedPartnerApplicationSchema)
    });


    const handleFormSubmit = async (data: any) => {
        setIsSubmitting(true);
        
        try {
            const formData = new FormData();
            
            if (data.logo && data.logo.length > 0) {
                formData.append('logo', data.logo[0]);
            }
            
            Object.entries(data).forEach(([key, value]) => {
                if (key === 'logo') {
                    return;
                } else if (key === 'regions' || key === 'servicesNeeded') {
                    if (Array.isArray(value)) {
                        value.forEach(item => {
                            formData.append(`${key}[]`, item);
                        });
                    }
                } else {
                    formData.append(key, String(value));
                }
            });
            
            await new Promise(resolve => setTimeout(resolve, 2000));
            
            alert('Application submitted successfully!');
        } catch (error) {
            alert('Failed to submit application. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };


    return (
        <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
            <div className="flex justify-center mb-8">
                <LogoHeader height='80px' width='200px' />
            </div>


            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-2 text-center">
                    Trusted Partner Application
                </h1>
                <p className="text-gray-600 text-center">
                    Apply to become a Client Vitals trusted partner and expand your business opportunities.
                </p>
            </div>


            <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
                <div className="border-b border-gray-200 pb-8">
                    <h2 className="text-xl font-semibold text-gray-900 mb-6">
                        Company Information
                    </h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <TextInputWithRegister
                            label="Company Name"
                            id="companyName"
                            placeholder="Enter company name..."
                            register={register("companyName")}
                            error={errors?.companyName?.message}
                        />


                        <TextInputWithRegister
                            label="Company Representative"
                            id="companyRep"
                            placeholder="Enter representative name..."
                            register={register("companyRep")}
                            error={errors?.companyRep?.message}
                        />
                    </div>


                    <TextInputWithRegister
                        label="Address"
                        id="address"
                        placeholder="Enter street address..."
                        register={register("address")}
                        error={errors?.address?.message}
                    />


                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <TextInputWithRegister
                            label="City"
                            id="city"
                            placeholder="Enter city..."
                            register={register("city")}
                            error={errors?.city?.message}
                        />


                        <TextInputWithRegister
                            label="State"
                            id="state"
                            placeholder="Enter state abbreviation..."
                            register={register("state")}
                            error={errors?.state?.message}
                        />


                        <TextInputWithRegister
                            label="Zip Code"
                            id="zip"
                            placeholder="Enter zip code..."
                            register={register("zip")}
                            error={errors?.zip?.message}
                        />
                    </div>


                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Service Regions <span className="text-red-500">*</span>
                        </label>
                        <MultiSelectDropdown
                            label=""
                            name="regions"
                            options={REGION_OPTIONS}
                            register={register}
                            error={errors?.regions?.message}
                            placeholder="Select regions..."
                            containerStyles="mb-0"
                        />
                    </div>
                </div>


                <div className="border-b border-gray-200 pb-8">
                    <h2 className="text-xl font-semibold text-gray-900 mb-6">
                        Contact Information
                    </h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <TextInputWithRegister
                            label="Phone Number"
                            id="phone"
                            placeholder="Enter phone number..."
                            register={register("phone")}
                            error={errors?.phone?.message}
                        />


                        <TextInputWithRegister
                            label="Email Address"
                            id="email"
                            placeholder="Enter email address..."
                            register={register("email")}
                            error={errors?.email?.message}
                        />
                    </div>


                    <TextInputWithRegister
                        label="Website URL"
                        id="websiteLink"
                        placeholder="https://www.yourcompany.com or www.yourcompany.com"
                        register={register("websiteLink")}
                        error={errors?.websiteLink?.message}
                    />
                </div>


                <div className="border-b border-gray-200 pb-8">
                    <h2 className="text-xl font-semibold text-gray-900 mb-6">
                        Business Details
                    </h2>
                    
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Employees <span className="text-red-500">*</span>
                        </label>
                        <p className="text-xs text-gray-500 mb-2">
                            Please choose the employee category that best describes your organization
                        </p>
                        <select
                            {...register("employees")}
                            className={`
                                block w-full px-3 py-2 border rounded-md shadow-sm
                                focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary
                                ${errors?.employees?.message
                                    ? 'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500'
                                    : 'border-gray-300'
                                }
                            `}
                        >
                            <option value="">Select employee count...</option>
                            {EMPLOYEE_COUNT_OPTIONS.map((option) => (
                                <option key={option.id} value={option.id}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                        {errors?.employees?.message && (
                            <p className="mt-1 text-sm text-red-600">{errors.employees.message}</p>
                        )}
                    </div>


                    <TextInputWithRegister
                        label="Number of Branches"
                        id="numberOfBranches"
                        placeholder="Enter number of branches..."
                        register={register("numberOfBranches")}
                        error={errors?.numberOfBranches?.message}
                    />


                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Services Needed <span className="text-red-500">*</span>
                        </label>
                        <MultiSelectDropdown
                            label=""
                            name="servicesNeeded"
                            options={SERVICE_OPTIONS}
                            register={register}
                            error={errors?.servicesNeeded?.message}
                            placeholder="Select services..."
                            containerStyles="mb-0"
                        />
                    </div>


                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Please Upload your Logo for our Website <span className="text-red-500">*</span>
                        </label>
                        <FileUpload
                            label=""
                            name="logo"
                            register={register}
                            error={errors?.logo?.message}
                            accept="image/png,image/jpg,image/jpeg"
                            containerStyles="mb-0"
                        />
                    </div>
                </div>


                <div className="pb-8">
                    <h2 className="text-xl font-semibold text-gray-900 mb-6">
                        Application Details
                    </h2>
                    
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Reason for Application <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            id="reasonForApplication"
                            rows={4}
                            placeholder="Please explain why you want to become a trusted partner..."
                            className={`
                                block w-full px-3 py-2 border rounded-md shadow-sm
                                focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary
                                ${errors?.reasonForApplication?.message
                                    ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500'
                                    : 'border-gray-300 placeholder-gray-400'
                                }
                            `}
                            {...register("reasonForApplication")}
                        />
                        {errors?.reasonForApplication?.message && (
                            <p className="mt-1 text-sm text-red-600">{errors.reasonForApplication.message}</p>
                        )}
                    </div>


                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Why do you want Client Vitals Services? <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            id="whyClientVitals"
                            rows={4}
                            placeholder="Please describe how Client Vitals services would benefit your business..."
                            className={`
                                block w-full px-3 py-2 border rounded-md shadow-sm
                                focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary
                                ${errors?.whyClientVitals?.message
                                    ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500'
                                    : 'border-gray-300 placeholder-gray-400'
                                }
                            `}
                            {...register("whyClientVitals")}
                        />
                        {errors?.whyClientVitals?.message && (
                            <p className="mt-1 text-sm text-red-600">{errors.whyClientVitals.message}</p>
                        )}
                    </div>
                </div>


                <div className="flex justify-end">
                    <Button
                        text={isSubmitting ? "Submitting..." : "Submit Application"}
                        type="submit"
                        disabled={isSubmitting}
                        styles="px-8 py-3"
                    />
                </div>
            </form>
        </div>
    );
};


export default TrustedPartnerApplicationForm;


